import { useEffect, useState, Dispatch, SetStateAction } from "react";

export default function useLocalStorage<T>(
  key: string,
  initialValue: T,
  engine = localStorage
): [T, Dispatch<SetStateAction<T>>] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = engine.getItem(key);

      if (!(key in engine) && initialValue !== undefined)
        engine.setItem(key, JSON.stringify(initialValue));

      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = (value: SetStateAction<T>) => {
    const valueToStore = value instanceof Function ? value(storedValue) : value;
    setStoredValue(valueToStore);
    engine.setItem(key, JSON.stringify(valueToStore));
  };

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === key && event.storageArea === engine) {
        const item = JSON.parse(engine.getItem(key)!);
        setStoredValue(item);
      }
    };

    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("storage", handler);
    };
  }, []);

  return [storedValue, setValue];
}
